import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/apiClient';
import { X, Loader2, ChevronDown } from 'lucide-react';
import {
  OPCIONES_CIERRE_MASTER,
  OPCIONES_TIPO_SERVICIO,
  OPCIONES_RESPONSABILIDAD,
  OPCIONES_CHOQUE_SIMPLE,
} from '../constants/masterData';

// ===============================================================
// 2. COMPONENTES INTERNOS
// ===============================================================

const TextInput = ({ label, name, value, onChange, disabled = false, type = "text" }) => {
  const labelClass = "block text-sm font-medium text-gray-300 mb-1";
  const inputClass = `w-full px-3 py-2 rounded-lg bg-gray-700 text-gray-100 border-2 border-gray-600 
                      focus:border-blue-500 focus:outline-none transition-colors ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`;
  return (
    <div>
      <label htmlFor={name} className={labelClass}>{label}</label>
      <input type={type} id={name} name={name} value={value || ''} onChange={onChange} className={inputClass} disabled={disabled} />
    </div>
  );
};

const SelectInput = ({ label, name, value, onChange, options, defaultOption }) => {
  const labelClass = "block text-sm font-medium text-gray-300 mb-1";
  const selectClass = "w-full px-3 py-2 rounded-lg bg-gray-700 text-gray-100 border-2 border-gray-600 focus:border-blue-500 focus:outline-none transition-colors";

  return (
    <div>
      <label htmlFor={name} className={labelClass}>{label}</label>
      <select id={name} name={name} value={value || ''} onChange={onChange} className={selectClass}>
        {defaultOption && <option value="">{defaultOption}</option>}
        {options.map((opt, index) => {
          // Soporta dos formas de opciones:
          // - objeto: { id, label }
          // - string: 'Nombre del abogado' (se usa como id y label)
          if (typeof opt === 'string') {
            const key = `${opt}-${index}`;
            return <option key={key} value={opt}>{opt}</option>;
          }
          const optId = opt.id !== undefined && opt.id !== null ? opt.id : opt.label || index;
          const optLabel = opt.label !== undefined && opt.label !== null ? opt.label : String(optId);
          return <option key={optId} value={optId}>{optLabel}</option>;
        })}
      </select>
    </div>
  );
};

const AccordionSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-700 last:border-b-0">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="flex justify-between items-center w-full p-4 text-left text-lg font-semibold text-gray-100 hover:bg-gray-700 transition-colors rounded-t-xl">
        {title}
        <ChevronDown size={24} className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="p-4 md:p-6 bg-gray-900/30"><div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">{children}</div></div>}
    </div>
  );
};

// ===============================================================
// 3. COMPONENTE PRINCIPAL DEL MODAL
// ===============================================================

const EditCaseModal = ({ isOpen, onClose, onSave, caseData, filterOptions,canEditExpediente }) => {
  const [editedCase, setEditedCase] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [aseguradoraActual, setAseguradoraActual] = useState('');

  // -----------------------------------------------------------------
  // OPCIONES DINÁMICAS DESDE API (con fallback a masterData)
  // -----------------------------------------------------------------
  const [dynamicOpciones, setDynamicOpciones] = useState(null); // null = aún no cargado
  const [loadingOpciones, setLoadingOpciones] = useState(false);

  // Cargar opciones dinámicas cuando se abre el modal y hay aseguradora
  useEffect(() => {
    if (!isOpen || !caseData?.aseguradora) return;
    let mounted = true;

    const fetchOpciones = async () => {
      setLoadingOpciones(true);
      try {
        // 1. Obtener lista de clientes configurados
        const clientes = await api.get('/clientes-config');
        const nombreAseg = (caseData.aseguradora || '').trim().toLowerCase();

        // Buscar el cliente que coincida por nombre
        const clienteMatch = (Array.isArray(clientes) ? clientes : []).find(c => {
          const nombreConfig = (c.nombre_display || c.nombre || '').trim().toLowerCase();
          return nombreConfig === nombreAseg || nombreAseg.includes(nombreConfig) || nombreConfig.includes(nombreAseg);
        });

        if (clienteMatch && clienteMatch.id) {
          // 2. Obtener opciones dinámicas para este cliente
          const opciones = await api.get(`/clientes-config/${clienteMatch.id}/opciones`);
          if (mounted && opciones) {
            console.log('[EditCaseModal] Opciones dinámicas cargadas para', caseData.aseguradora, opciones);
            setDynamicOpciones(opciones);
          }
        } else {
          console.log('[EditCaseModal] No se encontró config dinámica para', caseData.aseguradora, '- usando masterData');
          if (mounted) setDynamicOpciones(null);
        }
      } catch (e) {
        console.warn('[EditCaseModal] Error al cargar opciones dinámicas, usando masterData:', e);
        if (mounted) setDynamicOpciones(null);
      } finally {
        if (mounted) setLoadingOpciones(false);
      }
    };

    fetchOpciones();
    return () => { mounted = false; };
  }, [isOpen, caseData?.aseguradora]);

  // Funciones para obtener las listas de opciones — SOLO desde catálogo dinámico
  // del backend (anti-cruce IKE/Asissprex). Si no hay opciones dinámicas para
  // esta aseguradora, devolver lista vacía: el usuario verá "(sin opciones)"
  // en lugar de opciones equivocadas que llevan al cruce de códigos.
  const getOpcionesCierre = useCallback(() => {
    return dynamicOpciones?.cierre_servicio || [];
  }, [dynamicOpciones]);

  const getOpcionesTipoServicio = useCallback(() => {
    return dynamicOpciones?.tipo_servicio || [];
  }, [dynamicOpciones]);

  const getOpcionesResponsabilidad = useCallback(() => {
    return dynamicOpciones?.responsabilidad || [];
  }, [dynamicOpciones]);

  const getOpcionesChoqueSimple = useCallback(() => {
    return dynamicOpciones?.cierre_choque_simple || [];
  }, [dynamicOpciones]);

  // -----------------------------------------------------------------
  // EFECTO DE CARGA: LÓGICA INTELIGENTE POR ASEGURADORA
  // -----------------------------------------------------------------
  useEffect(() => {
    if (caseData) {
      setSaveError('');
      const safeCode = (val) => (val === null || val === undefined) ? '' : val;

      // 1. Detectar Aseguradora para saber qué IDs buscar (1... o 2...)
      const nombreAseguradora = (caseData.aseguradora || '').toUpperCase();
      const esIKE = nombreAseguradora.includes('IKE');
      const grupoObj = esIKE ? 'IKE' : 'ASIS';
      setAseguradoraActual(grupoObj);

      // 2. Función Helper: Busca el ID correcto basado en el texto y la aseguradora
      // SOLO usar opciones dinámicas — no fallback a masterData (anti-cruce).
      const listaCierre = dynamicOpciones?.cierre_servicio || [];
      const listaTipo = dynamicOpciones?.tipo_servicio || [];
      const listaResp = dynamicOpciones?.responsabilidad || [];
      const listaChoque = dynamicOpciones?.cierre_choque_simple || [];

      const recuperarID = (valor, lista, idOriginal) => {
        // Solo confiar en idOriginal si es realmente un ID numérico (no texto como 'SIN ASIGNAR')
        if (typeof idOriginal === 'number' && !isNaN(idOriginal)) return idOriginal;
        if (idOriginal !== null && idOriginal !== undefined && idOriginal !== ''
            && !isNaN(Number(String(idOriginal))) && String(idOriginal).trim() !== '') {
          return parseInt(idOriginal, 10);
        }

        // Si el valor ya es un número, devolverlo
        if (typeof valor === 'number' && !isNaN(valor)) return valor;
        if (!isNaN(valor) && valor !== '' && valor !== null && valor !== undefined) return parseInt(valor, 10);

        // Si es texto (ej: "TELEFONICO"), buscar el ID correspondiente a la aseguradora
        if (typeof valor === 'string' && valor.trim() === '') return '';
        if (typeof valor === 'string') {
          const limpio = valor.trim().toUpperCase();
          // Normalizar typos conocidos (ej: "ARREGLO DIRECTOO" → "ARREGLO DIRECTO")
          const normalizado = limpio.replace(/O{2,}$/, 'O').replace(/\s+/g, ' ');

          // 1. Coincidencia EXACTA en el grupo correcto (más confiable)
          let match = lista.find(opt =>
            opt.label.toUpperCase() === limpio &&
            (opt.grupo === grupoObj || opt.grupo === 'COMUN' || !opt.grupo)
          );

          // 2. Coincidencia exacta normalizada (typos)
          if (!match && normalizado !== limpio) {
            match = lista.find(opt =>
              opt.label.toUpperCase() === normalizado &&
              (opt.grupo === grupoObj || opt.grupo === 'COMUN' || !opt.grupo)
            );
          }

          // 3. Coincidencia parcial en el grupo correcto (datos legacy abreviados)
          if (!match) {
            match = lista.find(opt =>
              opt.label.toUpperCase().includes(limpio) &&
              (opt.grupo === grupoObj || opt.grupo === 'COMUN' || !opt.grupo)
            );
          }

          // 4. Último fallback: buscar en cualquier grupo
          if (!match) {
            match = lista.find(opt => opt.label.toUpperCase() === limpio);
          }
          if (!match) {
            match = lista.find(opt => opt.label.toUpperCase().includes(limpio));
          }

          if (match) return match.id;
        }
        return ''; // No encontrado
      };

      const idCierre = recuperarID(caseData.cierre_servicio, listaCierre, caseData.id_cierre_servicio);
      const idTipo = recuperarID(caseData.tipo_servicio, listaTipo, caseData.id_tipo_servicio);
      const idResp = recuperarID(caseData.responsabilidad, listaResp, caseData.id_responsabilidad);
      const idChoque = recuperarID(caseData.cierre_choque_simple, listaChoque, caseData.id_cierre_choque_simple);

      setEditedCase(prev => ({
        ...prev,
        ...caseData,

        expediente: caseData?.expediente || "",

        cierre_servicio: idCierre,
        tipo_servicio: idTipo,
        responsabilidad: idResp,
        cierre_choque_simple: idChoque,
        abogado: caseData.abogado_num_doc || caseData.abogado,

        observacion: safeCode(caseData.observacion),
        asunto: safeCode(caseData.asunto),
        conciliacion: safeCode(caseData.conciliacion),
        cliente_corporativo: safeCode(caseData.cliente_corporativo),
        hora_asignacion: safeCode(caseData.hora_asignacion),
        hora_llegada: safeCode(caseData.hora_llegada),
        direccion: safeCode(caseData.direccion)
      }));
    }
  }, [caseData, dynamicOpciones]);
  // Normaliza las opciones de abogado a formato { id, label }
  const normalizeAbogadoOptions = React.useCallback(() => {
    if (!filterOptions || !filterOptions.abogado) return [];
    return filterOptions.abogado.map((opt, idx) => {
      if (typeof opt === 'string') return { id: opt, label: opt };
      const id = opt.id !== undefined && opt.id !== null ? opt.id : (opt.label || `opt-${idx}`);
      const label = (opt.label !== undefined && opt.label !== null && String(opt.label).trim() !== '') ? opt.label : String(id);
      return { id, label };
    });
  }, [filterOptions]);

  // Estado local para opciones de abogado (puede venir de filterOptions o de la API /usuarios)
  const [abogadoOptionsLocal, setAbogadoOptionsLocal] = useState([]);

  // Siempre intentar obtener la lista de abogados desde la API al abrir el modal
  useEffect(() => {
    let mounted = true;
    const fetchUsuarios = async () => {
      try {
        console.log('[EditCaseModal] Solicitando lista de abogados desde /usuarios...');
        const usuarios = await api.get('/usuarios');
        console.log('[EditCaseModal] Respuesta de /usuarios:', usuarios);
        const abogadosLista = (Array.isArray(usuarios) ? usuarios : []).filter(u => (u.rol || '').toLowerCase() === 'abogado')
          .map(u => ({ id: u.num_doc, label: u.nombre }));
        if (mounted && abogadosLista.length > 0) {
          setAbogadoOptionsLocal(abogadosLista);
        } else if (mounted) {
          // Fallback: usar opciones de filterOptions si no hay abogados
          const inicial = normalizeAbogadoOptions().filter(o => !String(o.label).toLowerCase().includes('todos'));
          setAbogadoOptionsLocal(inicial);
        }
      } catch (e) {
        console.error('[EditCaseModal] Error al obtener /usuarios:', e);
        // Fallback: usar opciones de filterOptions si falla la API
        const inicial = normalizeAbogadoOptions().filter(o => !String(o.label).toLowerCase().includes('todos'));
        setAbogadoOptionsLocal(inicial);
      }
    };
    fetchUsuarios();
    return () => { mounted = false; };
  }, [isOpen, normalizeAbogadoOptions]);

  // Si el caso trae el nombre del abogado en lugar del num_doc, intentar mapearlo
  useEffect(() => {
    if (!caseData) return;
    // Esperar a que las opciones de abogados estén cargadas
    if (!abogadoOptionsLocal || abogadoOptionsLocal.length === 0) return;

    const currentAbogado = caseData.abogado || caseData.abogado_num_doc || '';
    if (!currentAbogado) return;

    // Si ya es un num_doc que coincide con alguna opción, no cambiar
    const existsById = abogadoOptionsLocal.find(o => String(o.id) === String(currentAbogado));
    if (existsById) {
      setEditedCase(prev => ({ ...prev, abogado: String(currentAbogado) }));
      return;
    }

    // Intentar encontrar por nombre (label)
    const matchByName = abogadoOptionsLocal.find(o => String(o.label).trim().toLowerCase() === String(currentAbogado).trim().toLowerCase());
    if (matchByName) {
      setEditedCase(prev => ({ ...prev, abogado: String(matchByName.id) }));
    }
  }, [caseData, abogadoOptionsLocal]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedCase(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveClick = async () => {
    setIsSaving(true);
    const parseSelect = (val) => {
      if (val === '' || val === undefined || val === null) return null;
      const parsed = parseInt(val, 10);
      return isNaN(parsed) ? null : parsed; // NaN nunca debe llegar al backend
    };
    // Aseguramos que se envíen los IDs numéricos
    const idCierre = parseSelect(editedCase.cierre_servicio);
    const idTipo = parseSelect(editedCase.tipo_servicio);
    const idResp = parseSelect(editedCase.responsabilidad);
    const idChoque = parseSelect(editedCase.cierre_choque_simple);

    // Obtener etiquetas para campos auxiliares si el back los pide
    const listaCierreActual = getOpcionesCierre();
    const getLabel = (id, list) => {
      const item = list.find(x => x.id === id);
      return item ? item.label : '';
    };

    const payloadCompleto = {
      ...editedCase,
      // Campos auxiliares de texto
      descripcion_cierre: getLabel(idCierre, listaCierreActual),
      status_label: getLabel(idCierre, listaCierreActual)
    };

    // Enviar SIEMPRE los selects (incluso null) para permitir vaciar el valor
    // en BD cuando el usuario selecciona explícitamente "(seleccionar estado)".
    // Antes se hacía delete y el backend nunca recibía el cambio.
    payloadCompleto.cierre_servicio = idCierre;
    payloadCompleto.id_cierre_se = idCierre;
    payloadCompleto.tipo_servicio = idTipo;
    payloadCompleto.responsabilidad = idResp;
    payloadCompleto.cierre_choque_simple = idChoque;

    // Warning tipo<->cierre incoherente (no bloquea, solo pregunta).
    // Cierre 999 (CANCELADO) es válido para cualquier tipo: skip warning.
    if (idCierre !== null && idCierre !== 999 && idTipo !== null) {
      const cierreInfo = listaCierreActual.find(x => x.id === idCierre);
      const tipoCierre = cierreInfo?.id_tipo_servicio;
      if (tipoCierre && tipoCierre !== idTipo) {
        const listaTipoActual = getOpcionesTipoServicio();
        const labelCierre = cierreInfo?.label || idCierre;
        const labelTipoCierre = (listaTipoActual.find(x => x.id === tipoCierre) || {}).label || tipoCierre;
        const labelTipoCaso = (listaTipoActual.find(x => x.id === idTipo) || {}).label || idTipo;
        const ok = window.confirm(
          `El cierre "${labelCierre}" (${idCierre}) corresponde al tipo de servicio ` +
          `"${labelTipoCierre}" (${tipoCierre}), no a "${labelTipoCaso}" (${idTipo}). ` +
          `\n\n¿Quieres guardarlo así de todos modos?`
        );
        if (!ok) {
          setIsSaving(false);
          return;
        }
      }
    }


    // Log para debugging (visible en DevTools → Console)
    console.log('[EditCaseModal] Payload enviado al backend:', {
      placa: payloadCompleto.placa,
      nombre: payloadCompleto.nombre,
      direccion: payloadCompleto.direccion,
      cierre_servicio: payloadCompleto.cierre_servicio,
      tipo_servicio: payloadCompleto.tipo_servicio,
      responsabilidad: payloadCompleto.responsabilidad,
      cierre_choque_simple: payloadCompleto.cierre_choque_simple,
      aseguradora: payloadCompleto.aseguradora,
      expediente: payloadCompleto.expediente,
    });

    try {
      await onSave(payloadCompleto);
      setSaveError('');
    } catch (e) {
      console.error("Error al guardar:", e);
      // Casos.jsx ya construyó el mensaje con detalle del backend; usarlo tal cual.
      const detalleBackend = e?.backendMessage && e.backendMessage !== 'Ocurrió un error. Por favor, intenta nuevamente'
        ? e.backendMessage
        : null;
      const msg = detalleBackend
        ? `Error ${e?.status || ''}: ${detalleBackend}`
        : (e?.message || 'Error al guardar. Verifica la conexión y vuelve a intentar.');
      setSaveError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-40 flex items-center justify-center p-4 transition-opacity duration-300" onClick={onClose}>
      <div className="bg-gray-800 text-gray-100 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col transform transition-transform duration-300 scale-95" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-gray-700 bg-gray-900 rounded-t-2xl">
          <h2 className="text-xl font-bold text-blue-300 truncate">Editando: {caseData?.expediente} ({aseguradoraActual})</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-100 p-2 rounded-full hover:bg-gray-700 transition-colors" title="Cerrar"><X size={24} /></button>
        </div>

        <div className="overflow-y-auto flex-grow">
          <AccordionSection title="1. Datos del Cliente y Generales" defaultOpen={true}>
            <TextInput label="Expediente" name="expediente" value={editedCase.expediente} onChange={handleChange} disabled={!canEditExpediente} />
            <TextInput label="Aseguradora (No Editable)" name="aseguradora" value={editedCase.aseguradora} disabled={true} />
            <TextInput label="Fecha" name="fecha" value={editedCase.fecha} onChange={handleChange} type="date" />
            <TextInput label="Cuenta" name="cuenta" value={editedCase.cuenta} onChange={handleChange} />
            <TextInput label="Nombre del Cliente" name="nombre" value={editedCase.nombre} onChange={handleChange} />
            <TextInput label="Teléfono" name="telefono" value={editedCase.telefono} onChange={handleChange} />
            <TextInput label="Dirección" name="direccion" value={editedCase.direccion} onChange={handleChange} />
            <TextInput label="Placa" name="placa" value={editedCase.placa} onChange={handleChange} />
          </AccordionSection>

          <AccordionSection title="2. Información Legal y del Servicio">
            <SelectInput
              label="Tipo de Servicio"
              name="tipo_servicio"
              value={editedCase.tipo_servicio}
              onChange={handleChange}
              options={getOpcionesTipoServicio()}
              defaultOption={loadingOpciones ? "Cargando..." : "Seleccionar Servicio"}
            />
            <TextInput label="Asunto" name="asunto" value={editedCase.asunto} onChange={handleChange} />
            <SelectInput
              label="Abogado Asignado"
              name="abogado"
              value={editedCase.abogado}
              onChange={handleChange}
              options={abogadoOptionsLocal}
              defaultOption="Sin Asignar"
            />
            <TextInput label="Conciliación" name="conciliacion" value={editedCase.conciliacion} onChange={handleChange} />
            <TextInput label="Observación" name="observacion" value={editedCase.observacion} onChange={handleChange} />
          </AccordionSection>

          <AccordionSection title="3. Cierre y Tiempos">
            <SelectInput
              label="Cierre de Servicio (Estado)"
              name="cierre_servicio"
              value={editedCase.cierre_servicio}
              onChange={handleChange}
              options={getOpcionesCierre()}
              defaultOption={loadingOpciones ? "Cargando..." : "Seleccionar Estado"}
            />
            <SelectInput
              label="Responsabilidad"
              name="responsabilidad"
              value={editedCase.responsabilidad}
              onChange={handleChange}
              options={getOpcionesResponsabilidad()}
              defaultOption={loadingOpciones ? "Cargando..." : "Seleccionar Resp."}
            />
            <SelectInput
              label="Cierre Choque Simple"
              name="cierre_choque_simple"
              value={editedCase.cierre_choque_simple}
              onChange={handleChange}
              options={getOpcionesChoqueSimple()}
              defaultOption={loadingOpciones ? "Cargando..." : "Seleccionar Opción"}
            />

            <TextInput label="Cliente Corporativo" name="cliente_corporativo" value={editedCase.cliente_corporativo} onChange={handleChange} />
            <TextInput label="Hora de Asignación" name="hora_asignacion" value={editedCase.hora_asignacion} onChange={handleChange} />
            <TextInput label="Hora de Llegada" name="hora_llegada" value={editedCase.hora_llegada} onChange={handleChange} />
          </AccordionSection>
        </div>

        <div className="flex flex-col gap-2 p-6 mt-auto border-t border-gray-700 bg-gray-900 rounded-b-2xl">
          {saveError && (
            <div className="w-full bg-red-900/40 border border-red-600 text-red-300 text-sm rounded-lg px-4 py-2 flex items-center gap-2">
              <span className="text-red-400 font-bold">⚠</span>
              {saveError}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button onClick={onClose} disabled={isSaving} className="bg-gray-600 hover:bg-gray-500 text-white font-semibold py-2 px-4 rounded-xl transition-colors duration-200">Cancelar</button>
            <button onClick={handleSaveClick} disabled={isSaving} className="bg-blue-800 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-xl transition-colors duration-200 flex items-center">
              {isSaving && <Loader2 size={16} className="animate-spin mr-2" />}
              Guardar Cambios
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCaseModal;